import { Link } from "react-router";

import { Chip, ChipRow } from "../lib/Chip.tsx";
import { TileRow } from "../lib/Tiles.tsx";
import { ComparisonChart, TimeChart } from "./ReportingCharts.tsx";
import {
  REPORTING_COPY,
  REPORTING_PERIODS,
  REPORTING_PERIOD_LABELS,
  REPORTING_SORTS,
  REPORTING_SORT_LABELS,
} from "./reporting.constants.ts";
import type { ReportingRow } from "./reporting.model.ts";
import { useReportingController } from "./useReportingController.ts";
import "./gallery.css";
import "./reporting.css";

/**
 * Costs & Income: what this gallery owes ONDO, set against what its post-sale
 * covenants have earned it — OND-97.
 *
 * IT READS TWO LEDGERS AND WRITES NOTHING. Both are durable Postgres tables
 * with their own triggers — `ondo.registration_fees` raised with the sale,
 * `ondo.resale_settlements` written with the resale — and this screen is a
 * projection of them. There is no control here that records a payment or a
 * settlement: that is the Transaction Centre's and the settlements ledger's
 * business, behind the `admin` rung, because a reporting view does not grant
 * permission to mark money as moved.
 *
 * THE HIERARCHY IS THE TICKET'S: shared filters, then four labelled totals,
 * then the graphs, then the table the totals came from. The filters are
 * genuinely shared — the period and the currency drive the tiles, both charts,
 * the table and the export from one `toReportingView` call, which is how
 * "tables and exported data reconcile to the displayed totals" is made
 * structural rather than careful.
 *
 * THE TABLE HAS NO FILTERS OF ITS OWN, and that is why it always reconciles. It
 * sorts, and it discloses the voided rows — which are in no total by definition
 * and say so. A table-only filter would have narrowed the rows under four tiles
 * that had not moved, which is exactly the inconsistency the acceptance
 * criteria forbid.
 *
 * It carries no title of its own: the portal header above it does.
 */
export function Reporting({ workBase }: { workBase: string }) {
  const {
    view,
    periodKey,
    choosePeriod,
    custom,
    typeCustomFrom,
    typeCustomTo,
    chooseCurrency,
    sort,
    chooseSort,
    showVoid,
    toggleVoid,
    exportCsv,
    exporting,
    exportProblem,
  } = useReportingController(workBase);

  if (view.kind === "loading") {
    return (
      <p className="gallery__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="gallery__note gallery__note--error" role="alert">
        <h2 className="gallery__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  return (
    <div className="reporting">
      <p className="reporting__lead">{REPORTING_COPY.lead}</p>

      <section className="reporting__filters" aria-label={REPORTING_COPY.period.legend}>
        <ChipRow label={REPORTING_COPY.period.legend}>
          {REPORTING_PERIODS.map((key) => (
            <Chip key={key} pressed={periodKey === key} onClick={() => choosePeriod(key)}>
              {REPORTING_PERIOD_LABELS[key]}
            </Chip>
          ))}
        </ChipRow>

        {periodKey === "custom" ? (
          <div className="reporting__custom">
            <label className="reporting__field">
              <span className="reporting__field-label">
                {REPORTING_COPY.period.customFrom}
              </span>
              <input
                type="date"
                className="reporting__input"
                value={custom.from}
                onChange={(event) => typeCustomFrom(event.target.value)}
              />
            </label>
            <label className="reporting__field">
              <span className="reporting__field-label">{REPORTING_COPY.period.customTo}</span>
              <input
                type="date"
                className="reporting__input"
                value={custom.to}
                onChange={(event) => typeCustomTo(event.target.value)}
              />
            </label>
          </div>
        ) : null}

        {view.currencies.length > 0 ? (
          <ChipRow label={REPORTING_COPY.currency.legend}>
            {view.currencies.map((choice) => (
              <Chip
                key={choice.currency}
                pressed={view.currency === choice.currency}
                onClick={() => chooseCurrency(choice.currency)}
              >
                {choice.label}
              </Chip>
            ))}
          </ChipRow>
        ) : null}

        <p className="reporting__active">
          <strong className="reporting__active-period">{view.period.label}</strong>
          {view.period.rangeLabel ? ` · ${view.period.rangeLabel}` : ""}
          {view.currency ? ` · ${view.currency}` : ""}
        </p>
        <p className="reporting__basis">{REPORTING_COPY.basis}</p>
        <p className="reporting__basis">{REPORTING_COPY.utcNote}</p>
        <p className="reporting__basis">{REPORTING_COPY.currency.separate}</p>
      </section>

      {view.period.problem ? (
        <p className="gallery__note gallery__note--error" role="alert">
          {view.period.problem}
        </p>
      ) : null}

      {view.totals.length > 0 ? (
        <section className="reporting__totals">
          <h2 className="reporting__totals-heading">
            {REPORTING_COPY.totals.heading(view.currency ?? "")}
          </h2>
          <TileRow tiles={view.totals} />
          {view.netLine ? <p className="reporting__net">{view.netLine}</p> : null}
        </section>
      ) : null}

      {view.currency && view.position ? (
        <section className="reporting__charts">
          <ComparisonChart
            bars={view.comparison}
            currency={view.currency}
            label={view.comparisonLabel}
          />
          <TimeChart
            buckets={view.buckets}
            currency={view.currency}
            label={view.timeLabel}
            grouping={view.grouping}
          />
        </section>
      ) : null}

      <section className="reporting__records">
        <div className="reporting__records-head">
          <h2 className="reporting__records-title">{REPORTING_COPY.table.title}</h2>
          <div className="reporting__actions">
            <button
              type="button"
              className="reporting__button"
              onClick={exportCsv}
              disabled={exporting || view.rows.length + view.voidRows.length === 0}
            >
              {exporting ? REPORTING_COPY.export.working : REPORTING_COPY.export.csv}
            </button>
            {/* The printable report. The stylesheet's print rules keep the
                summary, the graphs and the table and drop the controls, so what
                comes out of the printer is the report and not the screen. */}
            <button type="button" className="reporting__button" onClick={() => window.print()}>
              {REPORTING_COPY.export.print}
            </button>
          </div>
        </div>
        <p className="reporting__export-note">{REPORTING_COPY.export.note}</p>
        {exportProblem ? (
          <p className="gallery__note gallery__note--error" role="alert">
            {exportProblem}
          </p>
        ) : null}

        {view.rows.length > 0 ? (
          <ChipRow label={REPORTING_COPY.table.sortLegend}>
            {REPORTING_SORTS.map((key) => (
              <Chip key={key} pressed={sort === key} onClick={() => chooseSort(key)}>
                {REPORTING_SORT_LABELS[key]}
              </Chip>
            ))}
          </ChipRow>
        ) : null}

        {view.emptyNote ? (
          <p className="reporting__empty">{view.emptyNote}</p>
        ) : (
          <RecordTable rows={view.rows} />
        )}

        {view.voidRows.length > 0 ? (
          <div className="reporting__void">
            <button type="button" className="reporting__disclose" onClick={toggleVoid}>
              {showVoid
                ? REPORTING_COPY.table.hideVoid
                : REPORTING_COPY.table.showVoid(view.voidRows.length)}
            </button>
            {showVoid ? (
              <>
                <p className="reporting__void-note">{REPORTING_COPY.table.voidNote}</p>
                <RecordTable rows={view.voidRows} />
              </>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

/**
 * The records, as a real table.
 *
 * A `<table>` rather than a list of cards, because this is the one object on
 * the screen a reader scans down a column of — and because it is the accessible
 * equivalent of the two charts above it, which only works if it is actually
 * tabular. The amount is right-aligned and the currency is its own cell for the
 * same reason the CSV keeps them apart: a column of figures is read by
 * comparing them, and a code glued to each one stops that working.
 *
 * It wraps in its own scroller: seven columns do not fit a phone, and a table
 * is the one thing on this screen allowed to be wider than the page.
 */
function RecordTable({ rows }: { rows: ReportingRow[] }) {
  return (
    <div className="reporting__table-scroll">
      <table className="reporting__table">
        <thead>
          <tr>
            <th scope="col">{REPORTING_COPY.table.columns.side}</th>
            <th scope="col">{REPORTING_COPY.table.columns.record}</th>
            <th scope="col">{REPORTING_COPY.table.columns.work}</th>
            <th scope="col">{REPORTING_COPY.table.columns.date}</th>
            <th scope="col">{REPORTING_COPY.table.columns.status}</th>
            <th scope="col" className="reporting__cell--figure">
              {REPORTING_COPY.table.columns.amount}
            </th>
            <th scope="col">{REPORTING_COPY.table.columns.currency}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className={`reporting__row reporting__row--${row.side}`}>
              <td>
                <span
                  className={`reporting__tag reporting__tag--${row.side}`}
                >
                  {row.sideLabel}
                </span>
              </td>
              <td className="reporting__cell--kind">{row.kindLabel}</td>
              <td>
                <Link className="reporting__link" to={row.href} title={REPORTING_COPY.table.openWork}>
                  {row.title}
                </Link>
                <span className="reporting__cell--meta">
                  {[row.attributedName, row.ondoCode].filter(Boolean).join(" · ")}
                </span>
              </td>
              <td>{row.date}</td>
              <td>
                <span className={`reporting__status reporting__status--${row.status}`}>
                  {row.statusLabel}
                </span>
              </td>
              <td className="reporting__cell--figure">{row.amount}</td>
              <td>{row.currency}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
